import type {
  EventNotification,
  NotificationEventType,
} from '@indutrust/notification-model';

const FACTORY_EDIT_EVENT_TYPES = new Set<NotificationEventType>([
  'capability_suggestion_approved',
  'capability_suggestion_rejected',
  'factory_rejected',
  'factory_submitted_for_approval',
  'verification_expiring',
]);

const PROJECT_EVENT_TYPES = new Set<NotificationEventType>([
  'project_factory_added',
  'project_factory_state_changed',
  'shortlisted_supplier_lost_certification',
]);

const DASHBOARD_EVENT_TYPES = new Set<NotificationEventType>([
  '2030_vision_kpi_threshold_crossed',
  'capability_gap_suggestion',
  'export_report_ready',
  'market_capability_growth',
  'market_search_volume_change',
  'monthly_kpi_digest',
  'monthly_zone_digest',
  'national_capability_milestone_reached',
  'peer_zone_benchmark_update',
  'policy_impact_report_ready',
  'profile_viewed',
]);

const ADMIN_EVENT_ROUTES: Readonly<
  Partial<Record<NotificationEventType, string[]>>
> = {
  account_provisioning_request: ['/backoffice', 'contacts'],
  capability_suggestion_submitted: ['/backoffice', 'capability-suggestions'],
  churn_risk_alert: ['/dashboard'],
  follow_up_sla_breach: ['/backoffice/follow-up'],
  machine_submitted_for_review: ['/backoffice', 'machines', 'pending'],
  payment_event: ['/dashboard'],
  submit_for_approval_arrival: ['/backoffice', 'factories', 'pending'],
  support_ticket_escalated: ['/contact-us'],
  support_ticket_opened: ['/contact-us'],
  vr_request_received: ['/dashboard'],
};

function factoryRoute(
  targetId: number,
  eventType: NotificationEventType | undefined,
  edit: boolean
): string[] {
  if (edit || (eventType && FACTORY_EDIT_EVENT_TYPES.has(eventType))) {
    return ['/factory', String(targetId), 'edit'];
  }
  return ['/factory', String(targetId)];
}

function resolveByTarget(
  targetType: string,
  targetId: number,
  eventType: NotificationEventType | undefined
): string[] | null {
  switch (targetType.toLowerCase()) {
    case 'factory':
      return factoryRoute(targetId, eventType, false);
    case 'project':
      return ['/projects', String(targetId)];
    default:
      return null;
  }
}

/**
 * Resolves an in-app route for a notification click-through.
 *
 * Returns `null` when the event has no safe navigation target (e.g. aggregate-only
 * signals without a resource id). Prefer {@link target_type}/{@link target_id} when
 * present; fall back to {@link event_type} heuristics.
 */
export function resolveNotificationRoute(
  notification: EventNotification
): string[] | null {
  const eventType = notification.event_type;
  const targetId = notification.target_id;
  const targetType = notification.target_type;

  if (eventType) {
    const adminRoute = ADMIN_EVENT_ROUTES[eventType];
    if (adminRoute) {
      return [...adminRoute];
    }

    if (DASHBOARD_EVENT_TYPES.has(eventType)) {
      return ['/dashboard'];
    }
  }

  if (targetType && targetId !== undefined) {
    const byTarget = resolveByTarget(targetType, targetId, eventType);
    if (byTarget) {
      return byTarget;
    }
  }

  if (!eventType) {
    return null;
  }

  if (targetId === undefined) {
    return null;
  }
  if (FACTORY_EDIT_EVENT_TYPES.has(eventType)) {
    return factoryRoute(targetId, eventType, true);
  }

  if (
    PROJECT_EVENT_TYPES.has(eventType) ||
    eventType.startsWith('project_') ||
    eventType.startsWith('shortlisted_supplier_')
  ) {
    return ['/projects', String(targetId)];
  }

  if (eventType.startsWith('factory_') || eventType.includes('factory')) {
    return factoryRoute(targetId, eventType, false);
  }

  return null;
}

/** Whether clicking the notification should navigate somewhere in-app. */
export function isNotificationNavigable(
  notification: EventNotification
): boolean {
  return resolveNotificationRoute(notification) !== null;
}

/** True for a normal left-click navigation (not ctrl/cmd/shift/alt, not middle-click). */
export function isSameTabLinkClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    !event.altKey
  );
}
